import { useCallback, useContext, useMemo } from 'react';
import type { ChatContextItem } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { ActivePageContext, ChatContextItemsContext } from '@trinity/chat-components/components/Chat/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useHeaderContext } from '../context';
import { useSetGlobalChatContext } from './useSetGlobalChatContext';

export type AgentMenuItem = {
  key: string;
  label: string;
  onClick: () => void;
};

type Params = {
  onClick?: () => unknown;
};

export const useChatAgentMenuItems = (params: Params): AgentMenuItem[] => {
  const dispatch = useContext(ChatStateDispatchContext);
  const { setPendingContextType, setPendingContext } = useHeaderContext();
  const brandIdentifier = useActiveBrandIdentifier();

  const handleEverythingSelected = useSetGlobalChatContext({
    onClick: params.onClick,
  });

  const { setPage } = useContext(ActivePageContext);

  const handleContextSelected = useCallback(
    (item: ChatContextItem) => {
      return () => {
        dispatch({
          type: 'instance-context-changed',
          payload: {
            context: {
              type: ChatContextType.Agent,
              data: item,
              metadata: {},
            },
            brandIdentifier,
          },
        });
        setPage(item.pageId ?? null);
        setPendingContextType(null);
        setPendingContext(null);
        params.onClick?.();
      };
    },
    [brandIdentifier, dispatch, params, setPendingContextType, setPendingContext, setPage]
  );

  const updatePendingAgent = useCallback(
    (item: ChatContextItem, type: ChatContextType) => () => {
      setPendingContextType(type);
      setPendingContext(item);
      params.onClick?.();
    },
    [params, setPendingContextType, setPendingContext]
  );

  const contextItems = useContext(ChatContextItemsContext);

  const getContextItemOnClick = useCallback(
    (item: ChatContextItem) => {
      if (item.agent.identifier === (ChatContextType.Global as string)) {
        return handleEverythingSelected;
      }

      if (
        [
          ChatContextType.Document,
          ChatContextType.Folder,
          ChatContextType.Transcript,
          ChatContextType.KolProfile,
          ChatContextType.Project,
          ChatContextType.Brand,
        ].includes(item.agent.identifier as ChatContextType)
      ) {
        return updatePendingAgent(item, item.agent.identifier as ChatContextType);
      }

      if (item.subjectAutocomplete?.required) {
        return updatePendingAgent(item, ChatContextType.Agent);
      }

      return handleContextSelected(item);
    },
    [handleContextSelected, handleEverythingSelected, updatePendingAgent]
  );

  return useMemo(() => {
    return contextItems.map((item) => ({
      key: item.identifier,
      label: item.name,
      onClick: getContextItemOnClick(item),
    }));
  }, [contextItems, getContextItemOnClick]);
};
